import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  PencilSquareIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { getIconByName } from '../../utils/iconMapping';

const OffenceCard = ({
  offence,
  category = null,
  variant = 'button', // 'button', 'card', 'list'
  showLevel = true,
  displayLevel = null, // Override level to display (for multi-level offenses)
  showDescription = false,
  showUsageCount = false,
  showActions = false,
  clickable = false,
  selected = false,
  onClick,
  onEdit,
  onDelete,
  className = ''
}) => {
  if (!offence) return null;

  const getSeverityIcon = () => {
    switch (offence.severity) {
      case 'low':
        return <ExclamationCircleIcon className="w-4 h-4" />;
      case 'medium':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'high':
        return <ShieldExclamationIcon className="w-4 h-4" />;
      default:
        return <ExclamationCircleIcon className="w-4 h-4" />;
    }
  };

  const getSeverityColor = () => {
    switch (offence.severity) {
      case 'low':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'medium':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityBadgeColor = () => {
    switch (offence.severity) {
      case 'low':
        return 'badge-warning';
      case 'medium':
        return 'badge-warning'; // DaisyUI doesn't have badge-orange
      case 'high':
        return 'badge-error';
      default:
        return 'badge-neutral';
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (clickable && onClick) {
      onClick(offence);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(offence);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(offence);
    }
  };

  // Button variant (for QuickIssue selection)
  if (variant === 'button') {
    const categoryColor = category?.color;
    const categoryIcon = category?.icon;
    // FIXED: Recognitions have their own icon field (no category)
    const iconToShow = categoryIcon || offence.icon;

    return (
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center gap-2 px-3 py-2 rounded-xl shadow-sm transition-all duration-200
          ${!selected && 'bg-base-100 hover:bg-base-200/50'}
          ${clickable ? 'cursor-pointer' : 'cursor-default'}
          ${className}
        `}
        style={
          selected && categoryColor
            ? {
                backgroundColor: `${categoryColor}30`,
                color: categoryColor
              }
            : {}
        }
        disabled={!clickable}
      >
        {iconToShow ? (
          getIconByName(iconToShow, "w-4 h-4")
        ) : showLevel ? (
          getSeverityIcon()
        ) : null}
        <span className="text-sm font-medium">{offence.name || offence.offenseName}</span>
        {showLevel && offence.level && (
          <span
            className="badge badge-xs badge-outline"
            style={{
              borderColor: categoryColor || undefined,
              color: categoryColor || undefined
            }}
          >
            L{offence.level}
          </span>
        )}
      </button>
    );
  }

  // Card variant (for detailed view)
  if (variant === 'card') {
    return (
      <div 
        className={`
          card bg-base-100 shadow-sm border border-base-300 transition-all duration-200
          ${clickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
          ${selected ? 'ring-2 ring-primary' : ''}
          ${className}
        `}
        onClick={handleClick}
      >
        <div className="card-body p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {showLevel && (
                <div className={`p-1 rounded ${getSeverityColor().split(' ')[1]} ${getSeverityColor().split(' ')[2]}`}>
                  {getSeverityIcon()}
                </div>
              )}
              <div>
                <h4 className="font-semibold text-base-content">{offence.name || offence.offenseName}</h4>
                {showLevel && offence.severity && (
                  <span className={`badge badge-xs ${getSeverityBadgeColor()}`}>
                    {offence.severity}
                  </span>
                )}
              </div>
            </div>
            
            {showActions && (
              <div className="flex gap-1">
                {onEdit && (
                  <button 
                    onClick={handleEdit}
                    className="btn btn-ghost btn-circle btn-xs"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                )}
                {onDelete && (
                  <button 
                    onClick={handleDelete}
                    className="btn btn-ghost btn-circle btn-xs text-error hover:bg-error/10"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {showDescription && offence.description && (
            <p className="text-sm text-base-content/70 mt-2">{offence.description}</p>
          )}

          <div className="flex items-center justify-between mt-3 text-xs text-base-content/60">
            <span>Category: {offence.category || 'General'}</span>
            {showUsageCount && (
              <span>Used {offence.usageCount || 0} times</span>
            )}
            {offence.points && typeof offence.points === 'number' && (
              <span>{offence.points} points</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List variant (for compact listing)
  if (variant === 'list') {
    const categoryColor = category?.color;
    const categoryIcon = category?.icon;

    return (
      <div
        className={`
          flex items-center gap-3 p-3 rounded-lg border transition-colors
          ${clickable ? 'cursor-pointer hover:bg-base-200' : ''}
          ${selected ? 'ring-2 ring-primary bg-primary/5' : ''}
          ${className}
        `}
        style={{
          backgroundColor: categoryColor ? `${categoryColor}15` : undefined,
          borderColor: categoryColor || undefined
        }}
        onClick={handleClick}
      >
        {category && categoryIcon && (
          <div className="flex-shrink-0" style={{ color: categoryColor }}>
            {getIconByName(categoryIcon, "w-4 h-4")}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-base-content truncate">{offence.name || offence.offenseName}</h4>
            {offence.level && (
              <span className="badge badge-xs badge-outline">
                L{offence.level}
              </span>
            )}
          </div>

          {showDescription && offence.description && (
            <p className="text-sm text-base-content/70 truncate mt-1">{offence.description}</p>
          )}
        </div>

        {showActions && (
          <div className="flex gap-1 ml-2">
            {onEdit && (
              <button 
                onClick={handleEdit}
                className="btn btn-ghost btn-circle btn-xs"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button 
                onClick={handleDelete}
                className="btn btn-ghost btn-circle btn-xs text-error hover:bg-error/10"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default OffenceCard;